import {
  CurecallDropdownButton,
  CurecallList,
  CurecallListHeaders,
  ECurecallNotificationAppearance,
  ECurecallSize,
} from '@honestica/curecall-ui'
import { ActivateCell, IdentityCell, TitleIconCell } from './medical-staff-members-cells.component'
import { useTranslation } from 'react-i18next'
import { ReactNode } from 'react'
import { TMedicalStaffMember } from 'src/types/medical-staff-member'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { MedicalStaffMemberService } from 'src/services/medical-staff-member.service'
import { useNotification } from 'src/providers/notification.provider'
import { useCurrentProject } from 'src/providers/current-project.provider'

export type Props = {
  medicalStaffMemberList: Array<TMedicalStaffMember> | undefined
  onActionHandle: (data: TMedicalStaffMember, action: 'UPDATE' | 'CREATE_USER') => void
  isLoading: boolean
}

const ActionsCell = (props: {
  updateLabel: string
  createUserLabel: string
  dropdownButtonLabel: string
  onUpdateUserClick: VoidFunction
  onCreateUserClick: VoidFunction
}) => {
  return (
    <CurecallDropdownButton
      dropdownButtonLabel={props.dropdownButtonLabel}
      size={ECurecallSize.SMALL}
      items={[
        { label: props.updateLabel, onClick: props.onUpdateUserClick },
        { label: props.createUserLabel, onClick: props.onCreateUserClick },
      ]}
    />
  )
}

export default function MedicalStaffMemberList({
  medicalStaffMemberList,
  onActionHandle,
  isLoading,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { notify } = useNotification()
  const queryClient = useQueryClient()
  const { project } = useCurrentProject()
  const triggerNotification = (staffMember: TMedicalStaffMember): void => {
    const { firstname, lastname, civility } = staffMember
    const { title, message } = {
      title: staffMember.isActive
        ? t('pages.users-page.notify-medical-staff-activated-title')
        : t('pages.users-page.notify-medical-staff-disabled-title'),
      message: staffMember.isActive
        ? t('pages.users-page.notify-user-activated-description', { firstname, lastname, civility })
        : t('pages.users-page.notify-user-disabled-description', { firstname, lastname, civility }),
    }
    notify({
      title,
      message,
      appearance: ECurecallNotificationAppearance.SUCCESS,
    })
  }

  const { mutate: activateStaffMember } = useMutation({
    mutationKey: ['update-medical-staff-member'],
    mutationFn: (data: { staffMemberId: number; body: Partial<TMedicalStaffMember> }) =>
      MedicalStaffMemberService.updateMedicalStaffMember(project.id, data.staffMemberId, data.body),
    onError: (error: any) => {
      notify({
        title: t('common.actions.error'),
        message: error,
        appearance: ECurecallNotificationAppearance.ERROR,
      })
    },
    onSuccess: (updatedMedicalStaffMember: TMedicalStaffMember) => {
      triggerNotification(updatedMedicalStaffMember)
      queryClient.setQueryData(
        ['medical-staff-member-list'],
        (oldData: Array<TMedicalStaffMember> | undefined) => {
          return oldData?.map((staffMember) => {
            return staffMember.id === updatedMedicalStaffMember.id ? updatedMedicalStaffMember : staffMember
          })
        },
      )
    },
  })

  const tableRowsData =
    medicalStaffMemberList?.map((staffMember) => ({
      // Change the order will affect the table columns order
      active: (
        <ActivateCell
          tooltipLabel={
            staffMember.isActive
              ? t('pages.users-page.tooltip-medical-staff-member-actived')
              : t('pages.users-page.tooltip-medical-staff-member-disabled')
          }
          isMedicalStaffMemberActive={!!staffMember.isActive}
          onActivateMedicalStaffMember={() =>
            activateStaffMember({ staffMemberId: staffMember.id, body: { isActive: !staffMember.isActive } })
          }
        />
      ),
      id: staffMember.id,
      remoteId: staffMember.remoteId ?? '—',
      title: TitleIconCell({ civility: staffMember.civility }),
      'medical-staff-member': {
        observedData: staffMember.lastname,
        component: IdentityCell({
          civility: staffMember.civility,
          firstname: staffMember.firstname,
          lastname: staffMember.lastname,
          professionalCode: staffMember.professionalCode,
        }),
      },
      email: staffMember.email ?? staffMember?.email,
      phone: staffMember.phone ?? staffMember?.phone,
      rpps: staffMember.rpps,
      actions: ActionsCell({
        updateLabel: t('pages.users-page.edit'),
        createUserLabel: t('pages.users-page.add-user-account'),
        dropdownButtonLabel: t('pages.projects-page.actions'),
        onCreateUserClick: () => onActionHandle(staffMember, 'CREATE_USER'),
        onUpdateUserClick: () => onActionHandle(staffMember, 'UPDATE'),
      }),
    })) ?? []

  const tableRowsKeyList = tableRowsData[0]
    ? (Object.keys(tableRowsData[0]) as Array<keyof (typeof tableRowsData)[number]>)
    : []

  const tableColumnsData: CurecallListHeaders<Record<keyof (typeof tableRowsData)[number], ReactNode>> =
    tableRowsKeyList.map((columnKey) => ({
      key: columnKey,
      headerClassName: columnKey === 'actions' ? '[&>*]:c-ml-5 c-w-fit' : '',
      cellClassName: 'tw-align-middle',
      label: t(`pages.users-page.${columnKey}`),
      isSorted: columnKey === 'medical-staff-member',
    }))

  return (
    <CurecallList
      isLoading={isLoading}
      rowsData={tableRowsData}
      headersData={tableColumnsData}
      defaultSortData={{ key: 'medical-staff-member', descOrder: false }}
      loadingLabel={t('pages.users-page.table-loading')}
      noDataLabel={t('pages.users-page.table-no-medical-staff-data')}
    />
  )
}
